import { HTMLAttributes, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { applyBreakpoint } from "~/utils/Helpers";

const FONT_SIZE_CLASSES = {
    "3xl": "text-[108px]",
    "2xl": "text-[72px]",
    "xl": "text-[58px]",
    "lg": "text-[48px]",
    "md": "text-[40px]",
    "sm": "text-[32px]",
    "xs": "text-[24px]",
    "2xs": "text-[18px]",
    "3xs": "text-[16px]",
} as const;

const headingVariants = cva("", {
    variants: {
        size: FONT_SIZE_CLASSES,
        font: {
            "suisseBP": "font-suisseBP -tracking-[0.01em]",
            "items": "font-items",
            "featureDisplay": "font-featureDisplay",
        },
        weight: {
            "300": "font-light",
            "400": "font-normal",
            "500": "font-medium",
        },
        style: {
            "normal": "",
            "italic": "italic",
        },
    },
    defaultVariants: {
        size: "md",
        font: "suisseBP",
        weight: "300",
        style: "normal",
    },
});

type ResponsiveSize = {
    sm?: VariantProps<typeof headingVariants>["size"];
    md?: VariantProps<typeof headingVariants>["size"];
    lg?: VariantProps<typeof headingVariants>["size"];
    xl?: VariantProps<typeof headingVariants>["size"];
};

type Props = HTMLAttributes<HTMLHeadingElement> & VariantProps<typeof headingVariants> & {
    children?: ReactNode;
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "sub" | "sup" | "i" | "em";
    className?: string;
    sizes?: ResponsiveSize;
}

export const Heading = ({ children, as: Tag = "h2", className, sizes = {}, size, font, weight, style, ...props }: Props) => {
    const responsiveClasses = Object.entries(sizes).map(([bp, variant]) => {
        return applyBreakpoint(bp, [FONT_SIZE_CLASSES[variant!]]);
    }).join(" ");

    return (
        <Tag {...props} className={cn(headingVariants({ size, font, weight, style }), responsiveClasses, "leading-[1.1]", className)}>{children}</Tag>
    );
};

export default Heading;
